import { Component, OnInit, NgZone } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material';
import { UUID } from 'angular2-uuid';
import { Observable } from 'rxjs/Rx';

import { AsperaService } from '../aspera/aspera.service';
import { DownloadsService } from './downloads.service';
import { TransfersService } from '../transfers/transfers.service';
import { Transfer } from '../transfers/transfer.model';
import { TransferFile } from '../transfers/transfer-file.model';
import { FadeInAnimation } from '../shared/fade-in.animation';
import { ListItemAnimation } from '../shared/list-item.animation';
import { TimeOrDatePipe } from '../shared/time-or-date.pipe';
import { SuccessDialogComponent } from '../shared/success-dialog.component';
import { ErrorDialogComponent } from '../shared/error-dialog.component';

interface ActiveTransfer {
  files: TransferFile[];
  transfer: Transfer;
}

@Component({
  selector: 'app-downloads',
  templateUrl: './downloads.component.html',
  styleUrls: ['./downloads.component.scss'],
  animations: [ListItemAnimation(100), FadeInAnimation]
})
export class DownloadsComponent implements OnInit {

  asperaRunning: boolean = this.asperaService.running;
  activeTransfers: ActiveTransfer[] = [];
  downloads: Transfer[] = [];
  loading: boolean = true;
  newCount: number = 0;
  selected: Transfer;
  page: number;

  constructor(
    private asperaService: AsperaService,
    private dialog: MatDialog,
    private ngZone: NgZone,
    private route: ActivatedRoute,
    private downloadsService: DownloadsService,
    private transfersService: TransfersService) { }

  ngOnInit() {
    this.route.queryParams.subscribe((params: Params) => {
      this.selectTransfer(params.s);
      let newPage = (params.p != null) ? +params.p : 1;
      if (this.page !== newPage) {
        this.page = newPage;
        this.loadPage(newPage, params.s);
      }
    });

    this.loadAspera();
  }

  loadAspera(onSuccess?: () => void, onFailure?: () => void) {
    this.asperaService.startSession(() => {
      this.asperaRunning = this.asperaService.running;
      if (this.asperaRunning) {
        if (onSuccess != null) { onSuccess() }
      } else {
        if (onFailure != null) { onFailure() }
      }
    });
  }

  loadPage(page: number, selectedId?: string) {
    this.loading = true;
    this.downloads = [];
    this.downloadsService.getDownloads().subscribe(
      (data: any) => {
        this.downloads = data;
        this.updateNewCount();
        this.loading = false;
        this.selectTransfer(selectedId);
      }
    )
  }

  selectTransfer(selectedId: string) {
    this.selected = this.downloads.find((h) => h.uploadPackageGuid === selectedId)
  }

  onDownloadFiles(transfer: Transfer, files: TransferFile[]) {
    const active = {
      files: files.filter((f) => !f.expired),
      transfer: transfer
    };
    const download = () => {
      this.activeTransfers.push(active);
      this.downloadFiles(active).subscribe();
    };

    if (this.asperaRunning) {
      download();
    } else {
      this.loadAspera(
        () => download(),
        () => this.ngZone.run(() => this.showErrorDialog('Aspera Failed to Launch'))
      );
    }
  }

  downloadFiles(active: ActiveTransfer): Observable<any> {
    return this.transfersService.transferFiles(
      "download",
      active.transfer.uploadPackageGuid,
      active.transfer.uploadPackageId,
      active.files
    ).map((response) => {
      this.onTransferStart(active);
    }).catch((error) => {
      console.log("Transfer failed: ", error);
      this.onTransferEnd("failed", active);
      return Observable.throw(error);
    })
  }

  onDownloadNewFiles() {
    let download = () => {
      this.downloadNewFiles().subscribe()
    }

    if (this.asperaRunning) {
      download();
    } else {
      this.loadAspera(
        () => download(),
        () => this.ngZone.run(() => this.showErrorDialog("Aspera Failed to Launch"))
      )
    }
  }

  downloadNewFiles(): Observable<any> {
    let observable = Observable.from([null]);
    for (let transfer of this.downloads) {
      if (!transfer.downloaded) {
        let files: TransferFile[] = [];
        for (let file of transfer.files) {
          if (!file.downloaded && !file.expired) {
            files.push(file)
          }
        }
        let active = {
          files: files,
          transfer: transfer
        }
        this.activeTransfers.push(active);
        observable = observable.flatMap((response) => {
          return this.downloadFiles(active);
        })
      }
    }
    return observable;
  }

  onTransferStart(active: ActiveTransfer) {
    this.updateDownloadStatus(active);
    this.updateNewCount();
  }

  onTransferEnd(status: string, active: ActiveTransfer) {
    let index = this.activeTransfers.indexOf(active);
    if (index > -1) { this.activeTransfers.splice(index,1) }
    if (status === "completed") {
      if (this.activeTransfers.length == 0) {
        this.ngZone.run(() => {this.showSuccessDialog("Files Successfully Downloaded")})
      }
    } else {
      this.ngZone.run(() => {this.showErrorDialog("Failed to Download Files")})
    }
  }

  showSuccessDialog(message: string) {
    let dialogRef = this.dialog.open(SuccessDialogComponent, {
      height: '240px',
      width: '320px',
      data: {message: message}
    });
    setTimeout(() => dialogRef.close(), 3000);
  }

  showErrorDialog(message: string) {
    let dialogRef = this.dialog.open(ErrorDialogComponent, {
      height: '240px',
      width: '320px',
      data: {message: message}
    });
    setTimeout(() => dialogRef.close(), 6000);
  }

  updateDownloadStatus(active: ActiveTransfer) {
    for (let file of active.files) {file.downloaded = true;}
    active.transfer.downloaded = (active.transfer.files.find((f) => !f.downloaded) == null)
  }

  updateNewCount() {
    let count = 0;
    for (let transfer of this.downloads) {
      if (transfer.status === "completed" && !transfer.downloaded) {
        for (let file of transfer.files) {
          if (!file.downloaded && !file.expired) {
            count += 1;
          }
       }
      }
    }
    this.newCount = count;
  }

}



// WEBPACK FOOTER //
// ./src/app/downloads/downloads.component.ts